import { useParams, useNavigate } from 'react-router-dom'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { environment } from '../environment'
import {
  CompanyRoleBadge,
  SearchInput,
  transactionExportToCSV,
  TransactionsTabView,
  LoadingPage,
  ButtonVariant,
  DepositWalletsDrawer,
  FallbackCompanyIcon,
  ApproveMfaDialog,
} from '@valiant/ui-shared'
import type { FilterStatus, TransactionData } from '@valiant/ui-shared'
import {
  useGetTransactionsQuery,
  type TransactionStatus,
  type TransactionType,
  useRejectSwapMutation,
  useApproveSwapMutation,
  useGetCompanyDepositWalletsQuery,
} from '../generated/graphql'
import { useDebounce } from '@valiant/ui-shared'
import { useNavigation } from '../contexts/NavigationContext'
import { toast } from 'sonner'
import { MainLayout } from '../components/layout/MainLayout'
import { Wallet } from 'lucide-react'
import { useAuth } from '../AuthProvider'

export function CompanyActivity() {
  const { auth } = useAuth()
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const { selectedCompany, selectedCompanyId, setSelectedCompanyId, isSwapMode } = useNavigation()

  const [searchInput, setSearchInput] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<FilterStatus | null>(null)
  const [selectedType, setSelectedType] = useState<TransactionType | null>(null)
  const [isFlagged, setIsFlagged] = useState<boolean | null>(null)
  const [isWalletsDrawerOpen, setIsWalletsDrawerOpen] = useState(false)

  // MFA dialog state
  const [showMfaDialog, setShowMfaDialog] = useState(false)
  const [selectedSwapId, setSelectedSwapId] = useState<string | null>(null)
  const [isApproving, setIsApproving] = useState(false)

  const [{ data: walletsData, fetching: walletsLoading }, fetchWallets] =
    useGetCompanyDepositWalletsQuery({
      pause: true, // Only fetch when requested
      variables: { companyId: companyId! },
    })
  const debouncedSearch = useDebounce(searchInput)

  const filter = useMemo(() => {
    interface FilterObject {
      search?: string
      status?: TransactionStatus
      type?: TransactionType
      companyId?: string
      isFlagged?: boolean
    }

    const filterObj: FilterObject = {}
    if (debouncedSearch.trim()) filterObj.search = debouncedSearch.trim()
    if (selectedStatus) filterObj.status = selectedStatus as unknown as TransactionStatus
    if (selectedType) filterObj.type = selectedType
    if (isFlagged !== null) filterObj.isFlagged = isFlagged
    if (companyId) filterObj.companyId = companyId
    return Object.keys(filterObj).length > 0 ? filterObj : undefined
  }, [debouncedSearch, selectedStatus, selectedType, isFlagged, companyId])

  const [{ data: transactionsData, fetching: transactionsLoading }] = useGetTransactionsQuery({
    variables: { filter },
  })

  useEffect(() => {
    if (companyId && selectedCompanyId !== companyId) {
      setSelectedCompanyId(companyId)
    }
  }, [companyId, selectedCompanyId, setSelectedCompanyId])

  const transactions: TransactionData[] = transactionsData?.getTransactions?.nodes || []

  const handleViewDetails = (transactionId: string, type: string) => {
    if (type === 'DEPOSIT') {
      navigate(`/transactions/deposit/${transactionId}`)
    } else if (type === 'TRADE') {
      navigate(`/transactions/swap/${transactionId}`)
    }
  }

  const exportToCSV = () => {
    transactionExportToCSV(transactions, environment.MSB)
  }

  const handleFilter = (
    status: FilterStatus | null,
    type: TransactionType | null,
    flagged: boolean | null = null,
  ) => {
    setSelectedStatus(status)
    setSelectedType(type)
    setIsFlagged(flagged)
  }

  const [, rejectSwap] = useRejectSwapMutation()
  const [, approveSwap] = useApproveSwapMutation()

  const handleApprove = (transactionId: string) => {
    const transaction = transactions.find((t) => t.id === transactionId)
    if (!transaction) {
      toast.error('Transaction not found')
      return
    }

    if (transaction.type === 'DEPOSIT') {
      toast.error('Deposit rejection is not available to users. Please contact an administrator.')
      return
    } else if (transaction.type === 'TRADE') {
      setSelectedSwapId(transactionId)
      setShowMfaDialog(true)
    } else {
      toast.error('Unknown transaction type')
    }
  }

  const handleApproveSwap = useCallback(
    async (proof: { mfaCode?: string; passkeyApproval?: string }) => {
      if (!selectedSwapId) return

      setIsApproving(true)
      try {
        const result = await approveSwap({ input: { swapId: selectedSwapId, ...proof } })
        if (result.error) {
          throw new Error(result.error.message)
        }
        if (result.data) {
          toast.success('Swap approved successfully')
          setShowMfaDialog(false)
          setSelectedSwapId(null)
        } else {
          toast.error('Failed to approve swap')
          throw new Error('Failed to approve swap')
        }
      } catch (error) {
        console.error('Error approving swap:', error)
        toast.error('Failed to approve swap')
      } finally {
        setIsApproving(false)
      }
    },
    [approveSwap, selectedSwapId],
  )

  const handleRejectTransaction = async (transactionId: string) => {
    const transaction = transactions.find((t) => t.id === transactionId)
    if (!transaction) {
      toast.error('Transaction not found')
      throw new Error('Transaction not found')
    }

    try {
      if (transaction.type === 'DEPOSIT') {
        toast.error('Deposit rejection is not available to users. Please contact an administrator.')
        return
      } else if (transaction.type === 'TRADE') {
        await rejectSwap({ input: { swapId: transactionId, reason: 'Rejected by admin' } })
        toast.success('Swap rejected successfully')
      } else {
        toast.error('Unknown transaction type')
        throw new Error('Unknown transaction type')
      }
    } catch (error) {
      toast.error('Failed to reject transaction')
      console.error('Error rejecting transaction:', error)
      throw error
    }
  }

  const handleCopyWalletAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Address copied to clipboard!')
  }

  const handleFetchDepositWallets = useCallback(
    (companyId?: string) => {
      if (companyId) fetchWallets({ companyId })
    },
    [fetchWallets],
  )

  const handleOpenWalletsDrawer = useCallback(() => {
    setIsWalletsDrawerOpen(true)
    handleFetchDepositWallets(companyId)
  }, [companyId, handleFetchDepositWallets])

  const buttons = useMemo(() => {
    return [
      {
        label: 'View Wallets',
        variant: ButtonVariant.MAIN_ACTION,
        onClick: handleOpenWalletsDrawer,
        icon: <Wallet className="h-3 w-3 md:h-4 md:w-4" />,
      },
    ]
  }, [handleOpenWalletsDrawer])

  const badges = useMemo(() => {
    return [
      {
        node: <CompanyRoleBadge tradeType={selectedCompany?.tradeType} />,
        direction: 'LEFT',
      },
    ]
  }, [selectedCompany])

  if (transactionsLoading) {
    return <LoadingPage />
  }

  return (
    <MainLayout
      modalsComponent={
        <>
          <DepositWalletsDrawer
            open={isWalletsDrawerOpen}
            onOpenChange={setIsWalletsDrawerOpen}
            companyName={selectedCompany?.company || ''}
            companyImageUrl={selectedCompany?.imageUrl || undefined}
            depositWallets={walletsData?.getCompanyDepositWallets || []}
            loading={walletsLoading}
            onCopyAddress={handleCopyWalletAddress}
          />
          <ApproveMfaDialog
            open={showMfaDialog}
            onOpenChange={setShowMfaDialog}
            onConfirm={handleApproveSwap}
            isSubmitting={isApproving}
            title="Approve Swap"
            apiUrl={environment.API_URL}
            token={auth?.token}
            action="approveSwap"
            targetKey={selectedSwapId ?? undefined}
          />
        </>
      }
      breadcrumbs={[
        {
          label: selectedCompany?.company || '',
          imageUrl: selectedCompany?.imageUrl || undefined,
          fallbackImage: <FallbackCompanyIcon />,
        },
      ]}
      headerTitle={selectedCompany?.company}
      headerImage={selectedCompany?.imageUrl || <FallbackCompanyIcon />}
      badges={badges}
      buttons={buttons}
    >
      <TransactionsTabView
        transactions={transactions}
        loading={transactionsLoading}
        selectedStatus={selectedStatus}
        selectedType={selectedType}
        isFlagged={isFlagged}
        onFilter={handleFilter}
        onViewTransactionDetails={handleViewDetails}
        onRejectTransaction={handleRejectTransaction}
        onApproveTransaction={handleApprove}
        onExportCSV={exportToCSV}
        showExportButton={true}
        showActionButtons={true}
        hideDepositActions={true}
        showFlagColumn={true}
        hideGroupColumn={isSwapMode}
        headerSearchInput={
          <SearchInput
            placeholder="Search"
            value={searchInput}
            className="w-[200px]"
            onChange={(e) => setSearchInput(e)}
          />
        }
      />
    </MainLayout>
  )
}
